<template>
  <div
    :id="id"
    v-observe-visibility="handleVisibilityChanged"
    :class="bemBlock"
  >
    <!-- When using a carousel with loop, always listen for carousel-click
    instead of image-click to avoid swiper errors -->
    <div
      ref="swiper"
      class="swiper"
      :class="bemElement('carousel')"
      @click="handleCarouselImageClick($event)"
    >
      <div class="swiper-wrapper">
        <div
          v-for="(item, index) in items"
          :key="`${id}-carousel-item-${index}`"
          class="swiper-slide"
          :class="[bemElement('item'), { active: index === 0 }]"
        >
          <slot
            v-if="shouldRender(index)"
            :item="item"
            :classname="bemElement('pic')"
            :index="index"
            loading="eager"
          >
            <BaseImage
              :class="bemElement('pic')"
              :image="item"
              :image-classes="bemElement('image')"
              loading="eager"
              @image-click="$emit('image-click', $event)"
            />
          </slot>
        </div>
      </div>
    </div>
    <button
      v-show="shouldShowControls"
      slot="button-prev"
      :class="bemElement('control', 'prev')"
      @click="goPrevious()"
    >
      <span
        class="icon-arrow-photos icon-arrow-photos--prev"
        aria-hidden="true"
      />
      <span class="sr-only">Previous</span>
    </button>
    <button
      v-show="shouldShowControls"
      slot="button-next"
      :class="bemElement('control', 'next')"
      @click="goNext()"
    >
      <span
        class="icon-arrow-photos icon-arrow-photos--next"
        aria-hidden="true"
      />
      <span class="sr-only">Next</span>
    </button>
  </div>
</template>

<script>
import nanoid from 'nanoid/non-secure';
import bemClassesMixin from '../mixins/bemClassesMixin';
import BaseImage from './BaseImage.vue';
import EventBusConfig from '../config/eventBusConfig';

export default {
  name: 'BaseCarousel',

  components: {
    BaseImage,
  },

  mixins: [
    bemClassesMixin,
  ],

  props: {
    areControlsShown: {
      default: true,
      type: Boolean,
    },
    items: {
      default: () => [],
      type: Array,
      required: true,
    },
    contextProductId: {
      default: null,
      type: String,
      required: false,
    },
    swiperOptions: {
      default: () => {},
      type: Object,
      required: false,
    },
  },

  data: () => ({
    id: `car-${nanoid()}`,
    isInteracted: false, // the user has interacted with the carousel
    isSwiperImported: false,
    isSwiperInitialized: false,
    isSwiperInitializing: false,
    shouldUpdatePosition: false,
    swiperInstance: null,
    swiperBehaviourOptions: {
      allowTouchMove: true,
      breakpoints: {},
      loop: true,
      preventClicks: false,
      preventClicksPropagation: false,
      slidesPerView: 1,
      spaceBetween: 1,
      touchStartPreventDefault: false,
    },
    swiperCurrentOptions: {},
  }),

  computed: {
    shouldShowControls() {
      return this.areControlsShown && this.swiperCurrentOptions?.slidesPerView < this.items.length;
    },
  },

  watch: {
    items(newSet) {
      // watch if it received new more items, and if the set as more than one item and is it not been initialized,
      // please init carousel
      if (newSet.length > 1) {
        this.$nextTick(() => {
          this.updateCarousel();
        });
      }
    },
    $windowWidth() {
      this.getCurrentSwiperOptions();
    },
  },

  beforeCreate() {
    /*
    The component is prepared to load Swiper dinamically. However, it is is used
    whenever a carousel is shown in viewport. That condition is met when loading
    PLP, so will force to load the partial at the beginning.
    Although this partial seems useless, it will be cached by the browser and it
    will have less modifications than having code of the project.
    */
    import(/* webpackChunkName: "swiper.js" */ 'swiper').then(() => {
      this.isSwiperImported = true;
    });
  },

  mounted() {
    this.swiperBehaviourOptions = { ...this.swiperBehaviourOptions, ...this.swiperOptions };
    this.getCurrentSwiperOptions();
    this.initializeSwiperIfIsVisible();
    this.$eventBus.$on(
      EventBusConfig.events.TheProductList.columnModeWillChange,
      this.destroySwiper,
    );
    this.$eventBus.$on(
      EventBusConfig.events.TheProductList.columnModeChanged,
      this.initializeSwiperIfIsVisible,
    );
    if (this.contextProductId) {
      this.$eventBus.$on('base-product-slider:ready', this.handleParentGliderUpdate);
      this.$eventBus.$on('base-product-slider:updated', this.handleParentGliderUpdate);
    }
  },

  beforeDestroy() {
    this.destroySwiper();
    this.$eventBus.$off(
      EventBusConfig.events.TheProductList.columnModeWillChange,
      this.destroySwiper,
    );
    this.$eventBus.$off(
      EventBusConfig.events.TheProductList.columnModeChanged,
      this.initializeSwiperIfIsVisible,
    );
    if (this.contextProductId) {
      this.$eventBus.$off('base-product-slider:ready', this.handleParentGliderUpdate);
      this.$eventBus.$off('base-product-slider:updated', this.handleParentGliderUpdate);
    }
  },

  methods: {
    destroySwiper() {
      if (!this.isSwiperInitialized) return;
      this.swiperInstance.off('sliderFirstMove', this.handleFirstInteraction);
      this.swiperInstance?.destroy();
      this.isSwiperInitialized = false;
    },

    // Get current applied options
    getCurrentSwiperOptions() {
      this.swiperCurrentOptions = this.swiperBehaviourOptions;
      const breakpoints = Object.keys(this.swiperCurrentOptions.breakpoints);
      if (breakpoints?.length > 0) {
        breakpoints.forEach((breakpoint) => {
          if (parseInt(breakpoint, 10) <= this.$windowWidth) {
            this.swiperCurrentOptions = {
              ...this.swiperCurrentOptions,
              ...this.swiperCurrentOptions.breakpoints[breakpoint],
            };
          }
        });
      }
    },

    goNext() {
      if (this.isSwiperInitialized) {
        this.handleFirstInteraction();
        this.shouldUpdatePosition = this.swiperInstance.realIndex === this.items.length - 1;
        this.swiperInstance.slideNext();
        if (this.shouldUpdatePosition) {
          this.swiperInstance.realIndex = 0;
          this.shouldUpdatePosition = false;
        }
      } else {
        this.initializeSwiper.then(this.goNext);
      }
    },

    goPrevious() {
      if (this.isSwiperInitialized) {
        this.handleFirstInteraction();
        this.shouldUpdatePosition = this.swiperInstance.realIndex === 0;
        this.swiperInstance.slidePrev();
        if (this.shouldUpdatePosition) {
          this.swiperInstance.realIndex = this.items.length - 1;
          this.shouldUpdatePosition = false;
        }
      } else {
        this.initializeSwiper.then(this.goPrevious);
      }
    },

    handleCarouselImageClick(event) {
      const currentIndex = this.swiperInstance?.realIndex;
      this.$emit('carousel-click', event, currentIndex);
    },

    handleFirstInteraction() {
      if (this.isInteracted) return;
      this.isInteracted = true;
      if (this.swiperCurrentOptions?.loop) {
        this.swiperInstance.loopDestroy();
        this.swiperInstance.loopCreate();
      }
    },

    /**
     * Update this carousel if it is nested inside a BaseProductSlider and it
     * receives the loaded event containing the ID of the product which is
     * associated with this carousel.
     * @param {object} payload
     * @param {string[]} payload.productIds
     */
    handleParentGliderUpdate({ productIds = [] }) {
      if (productIds.includes(this.contextProductId)) {
        this.updateCarousel();
      }
    },

    handleVisibilityChanged(isVisible) {
      if (isVisible) {
        this.initializeSwiper().catch((e) => {
          const isUnknownError = e.message !== 'Avoid reinitialization';
          // Throw not controlled rejections
          if (isUnknownError) throw e;
        });
      } else {
        this.destroySwiper();
      }
    },

    initializeSwiper() {
      const avoidInitalizeAgain = this.isSwiperInitialized || this.isSwiperInitializing;
      if (avoidInitalizeAgain) {
        return Promise.reject(new Error('Avoid reinitialization'));
      }
      this.isSwiperInitializing = true;
      return import(/* webpackChunkName: "swiper.js" */ 'swiper').then((module) => {
        const Swiper = module.default;
        // If any aditional module is needed
        // if (this.showProgressBar) {
        //   const { Scrollbar } = module;
        //   this.swiperBehaviourOptions.modules = [Scrollbar];
        //   this.swiperBehaviourOptions.scrollbar = { el: '.swiper-scrollbar', draggable: true };
        // }
        this.swiperInstance = new Swiper(
          this.$refs.swiper,
          this.swiperBehaviourOptions,
        );
        this.swiperInstance.once('sliderFirstMove', this.handleFirstInteraction);
        this.isSwiperInitializing = false;
        this.isSwiperInitialized = true;
        return this.swiperInstance;
      });
    },

    initializeSwiperIfIsVisible() {
      if (this.$isInViewport(this.$refs.swiper)) {
        this.initializeSwiper();
      }
    },

    /**
     * Elements that will be visible on init (always)
     * Double the initial loading amount for a good initial scrolling experience
     * If loop is enabled, last n elements (equal to the initial loading amount)
     */
    shouldRender(index) {
      const { slidesPerView } = this.swiperCurrentOptions;
      return this.isInteracted
        || (index <= slidesPerView
        || index + 1 <= slidesPerView * 2)
        || (this.swiperBehaviourOptions.loop && index >= this.items.length - slidesPerView);
    },

    updateCarousel() {
      if (this.isSwiperInitialized === true) {
        this.swiperInstance?.update();
      }
    },
  },
};
</script>
